import { useState, useEffect } from "react";
import { Key, CheckCircle, AlertCircle, RefreshCw, Users, Download, ChevronDown, ChevronUp } from "lucide-react";
import myobFirebaseService from "services/myobFirebaseService";
import { useAuthContext } from "context/auth-context";
import toast from "react-hot-toast";

const MYOBOneClickAuth = () => {
    const [isConnected, setIsConnected] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [connectionInfo, setConnectionInfo] = useState(null);
    const [tokenStatus, setTokenStatus] = useState(null);
    const { currentUserData } = useAuthContext();
    const [companyFiles, setCompanyFiles] = useState([]);
    const [allContacts, setAllContacts] = useState(null);
    const [isCollapsed, setIsCollapsed] = useState(true); // Start collapsed by default

    useEffect(() => {
        if (currentUserData?.uid) {
            myobFirebaseService.setCurrentUser(currentUserData.uid);
            checkConnectionStatus();
        }
    }, [currentUserData]);

    const checkConnectionStatus = async () => {
        try {
            const tokens = await myobFirebaseService.loadTokensFromFirebase();
            const connected = !!tokens;
            setIsConnected(connected);
            if (connected) {
                setConnectionInfo(myobFirebaseService.getTokensInfo());
                setTokenStatus({
                    hasAccessToken: !!tokens.access_token,
                    hasRefreshToken: !!tokens.refresh_token,
                    expiresAt: tokens.expires_at,
                    isExpired: Date.now() >= tokens.expires_at
                });
            }
        } catch (error) {
            console.error('Error checking connection status:', error);
            setIsConnected(false);
        }
    };

    const handleOneClickAuth = () => {
        setIsLoading(true);
        try {
            const state = myobFirebaseService.generateState();
            const url = myobFirebaseService.getAuthorizationUrl(state);
            // Open in same window for better UX
            window.location.href = url;
        } catch (error) {
            console.error('Error initiating auth:', error);
            toast.error('Failed to initiate MYOB authorization');
            setIsLoading(false);
        }
    };

    const handleDisconnect = async () => {
        if (window.confirm('Are you sure you want to disconnect from MYOB?')) {
            try {
                await myobFirebaseService.clearTokensFromFirebase();
                setIsConnected(false);
                setConnectionInfo(null);
                setTokenStatus(null);
                setCompanyFiles([]);
                setAllContacts(null);
                toast.success('Disconnected from MYOB');
            } catch (error) {
                toast.error('Failed to disconnect from MYOB');
            }
        }
    };

    const loadCompanyFiles = async () => {
        setIsLoading(true);
        try {
            const files = await myobFirebaseService.getCompanyFiles();
            setCompanyFiles(files?.Items || []);
            toast.success(`Found ${files?.Items?.length || 0} company files`);
        } catch (error) {
            console.error('Error loading company files:', error);
            toast.error('Failed to load company files');
        } finally {
            setIsLoading(false);
        }
    };

    const loadAllContacts = async (companyFileId) => {
        setIsLoading(true);
        try {
            // Note: getAllContacts method needs to be implemented in Firebase service
            // For now, we'll skip this functionality
            throw new Error('Contact loading not yet implemented in Firebase service');
        } catch (error) {
            console.error('Error loading contacts:', error);
            toast.error('Failed to load contacts');
        } finally {
            setIsLoading(false);
        }
    };

    const exportToCSV = (contacts, filename) => {
        if (!contacts) return;

        const allUsers = [
            ...contacts.employees.map(emp => ({
                Type: 'Employee',
                Name: emp.Name || 'N/A',
                Email: emp.Email || 'N/A',
                Phone: emp.Phone || 'N/A',
                Address: emp.Address || 'N/A',
                UID: emp.UID || 'N/A'
            })),
            ...contacts.customers.map(cust => ({
                Type: 'Customer',
                Name: cust.Name || 'N/A',
                Email: cust.Email || 'N/A',
                Phone: cust.Phone || 'N/A',
                Address: cust.Address || 'N/A',
                UID: cust.UID || 'N/A'
            }))
        ];

        const headers = ['Type', 'Name', 'Email', 'Phone', 'Address', 'UID'];
        const csvContent = [
            headers.join(','),
            ...allUsers.map(user => 
                headers.map(header => `"${(user[header] || '').toString().replace(/"/g, '""')}"`).join(',')
            )
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (!isConnected) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => setIsCollapsed(!isCollapsed)}
                >
                    <div className="flex items-center gap-2">
                        <Key className="text-blue-600" size={24} />
                        <h3 className="text-lg font-semibold text-gray-800">MYOB One-Click Authorization</h3>
                        <button 
                            className="ml-3 text-gray-500 hover:text-gray-700"
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsCollapsed(!isCollapsed);
                            }}
                            title={isCollapsed ? "Expand" : "Collapse"}
                        >
                            {isCollapsed ? (
                                <ChevronDown className="h-5 w-5" />
                            ) : (
                                <ChevronUp className="h-5 w-5" />
                            )}
                        </button>
                    </div>
                </div>
                
                {!isCollapsed && (
                    <div className="mt-4">
                        <div className="mb-4">
                            <p className="text-gray-600 mb-4">
                                Connect to MYOB with a single click. This will redirect you to MYOB for authorization, then bring you back with full access to all your data.
                            </p>
                            
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                                <h4 className="font-semibold text-blue-800 mb-2">What happens:</h4>
                                <ol className="list-decimal list-inside space-y-1 text-blue-700 text-sm">
                                    <li>Click "Authorize with MYOB" below</li>
                                    <li>Login to your MYOB account</li>
                                    <li>Grant permissions to access your data</li>
                                    <li>Automatically return with full access</li>
                                    <li>Access all employees and customers instantly</li>
                                </ol>
                            </div>
                        </div>
                        
                        <button
                            onClick={handleOneClickAuth}
                            disabled={isLoading}
                            className="flex items-center gap-2 w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? (
                                <>
                                    <RefreshCw className="animate-spin" size={20} />
                                    Redirecting to MYOB...
                                </>
                            ) : (
                                <>
                                    <Key size={20} />
                                    Authorize with MYOB
                                </>
                            )}
                        </button>
                        
                        <div className="mt-4 text-sm text-gray-500">
                            <p>This is a one-time setup. After authorization, you can access all MYOB data automatically.</p>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm border border-green-200 p-6">
            <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setIsCollapsed(!isCollapsed)}
            >
                <div className="flex items-center gap-2">
                    <CheckCircle className="text-green-600" size={24} />
                    <h3 className="text-lg font-semibold text-green-800">MYOB Connected</h3>
                    <button 
                        className="ml-3 text-gray-500 hover:text-gray-700"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsCollapsed(!isCollapsed);
                        }}
                        title={isCollapsed ? "Expand" : "Collapse"}
                    >
                        {isCollapsed ? (
                            <ChevronDown className="h-5 w-5" />
                        ) : (
                            <ChevronUp className="h-5 w-5" />
                        )}
                    </button>
                </div>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        handleDisconnect();
                    }}
                    className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                >
                    Disconnect
                </button>
            </div>

            {!isCollapsed && (
                <div className="mt-4">
                    {connectionInfo && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                            <h4 className="font-semibold text-green-800 mb-2">Connection Details:</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                <p><strong>Business:</strong> {connectionInfo.business_name}</p>
                                <p><strong>User:</strong> {connectionInfo.username}</p>
                                <p><strong>Scope:</strong> {connectionInfo.scope}</p>
                                <p><strong>Expires:</strong> {connectionInfo.expires_at}</p>
                            </div>
                        </div>
                    )}

                    {tokenStatus && (
                        <div className={`mb-4 p-3 rounded-lg ${
                            tokenStatus.status === 'valid' ? 'bg-green-50 border border-green-200' :
                            tokenStatus.status === 'expiring_soon' ? 'bg-yellow-50 border border-yellow-200' :
                            'bg-red-50 border border-red-200'
                        }`}>
                            <p className={`text-sm ${
                                tokenStatus.status === 'valid' ? 'text-green-700' :
                                tokenStatus.status === 'expiring_soon' ? 'text-yellow-700' :
                                'text-red-700'
                            }`}>
                                <strong>Token Status:</strong> {tokenStatus.message}
                            </p>
                        </div>
                    )}

                    {/* Company Files Section */}
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-gray-800">Company Files</h4>
                            <button
                                onClick={loadCompanyFiles}
                                disabled={isLoading}
                                className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50"
                            >
                                <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
                                Load Files
                            </button>
                        </div>

                        {companyFiles.length > 0 && (
                            <div className="space-y-2">
                                {companyFiles.map((file, index) => (
                                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <div>
                                            <p className="font-medium">{file.Name}</p>
                                            <p className="text-sm text-gray-600">ID: {file.URI}</p>
                                        </div>
                                        <button
                                            onClick={() => loadAllContacts(file.URI)}
                                            disabled={isLoading}
                                            className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 disabled:opacity-50"
                                        >
                                            Load Users
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Users List Section */}
                    {allContacts && (
                        <div className="mb-6">
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="font-medium text-gray-800">MYOB Users</h4>
                                <button
                                    onClick={() => exportToCSV(allContacts, `myob_users_${new Date().toISOString().split('T')[0]}.csv`)}
                                    className="flex items-center gap-1 px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                                >
                                    <Download size={14} />
                                    Export CSV
                                </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Users className="text-blue-600" size={16} />
                                        <span className="font-medium text-blue-800">Employees</span>
                                    </div>
                                    <p className="text-2xl font-bold text-blue-900">{allContacts.employees.length}</p>
                                </div>
                                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Users className="text-green-600" size={16} />
                                        <span className="font-medium text-green-800">Customers</span>
                                    </div>
                                    <p className="text-2xl font-bold text-green-900">{allContacts.customers.length}</p>
                                </div>
                            </div>

                            {/* Quick preview of users */}
                            <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                                {[...allContacts.employees.slice(0, 5), ...allContacts.customers.slice(0, 5)].map((user, index) => (
                                    <div key={index} className="p-2 border-b border-gray-100 last:border-b-0 text-sm">
                                        <span className="font-medium">{user.Name || 'N/A'}</span>
                                        <span className="text-gray-500 ml-2">({user.Email || 'No email'})</span>
                                    </div>
                                ))}
                                {(allContacts.employees.length + allContacts.customers.length) > 10 && (
                                    <div className="p-2 text-sm text-gray-500 text-center">
                                        ... and {(allContacts.employees.length + allContacts.customers.length) - 10} more users
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {isLoading && (
                        <div className="flex items-center justify-center py-4">
                            <RefreshCw className="animate-spin text-blue-600" size={24} />
                            <span className="ml-2 text-gray-600">Loading...</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MYOBOneClickAuth;
